// 角色库（作者 2026-11-02：“设计角色库，开局可以选择角色，每个角色有自己的口头禅，按自己的牌况自动播报语音，就像斗地主那样”）。
// 8 个角色：名字、Q版大头头像（public/avatars/<id>.webp）、外形（出图用）、音色（MiniMax 系统音色）、打法性格（BotStyle）与台词。
// 角色只说等待时的口头禅（催促、闲聊）；报牌与牌局事实由播报员说（见下方 CALL_LINES / NARRATOR）。
// 语音文件由 tools/voice_gen.mjs 按这里的台词合成到 public/voice/<id>/，缺文件时只显示文字气泡。名字已在专家库登记为作品角色。
import type { BotStyle } from './bot.ts';

/**
 * 语音分工（作者 2026-11-02 对齐）：角色自己的声音只说等待时的口头禅——轮到你太久时催促（urge）、等别人出牌时闲聊（idle）；
 * 出牌、不出、牌型、炸弹和牌局事实（上游、报牌、进贡、升级…）一律由播报员（同开场旁白一个声音）报。场景表见 docs/语音体系.md。
 */
export type LineEvent = 'urge' | 'idle';

export interface Character {
  id: string;
  name: string;
  avatar: string; // 头像图缺失时显示的一个字
  tint: string; // 头像圆底的淡色（取自角色主色，避开宣纸米黄，作者 11-02：适配头像的淡色底，别和弹窗撞色）
  tagline: string;
  title: string; // 江湖名号（作者 11-03：显示在昵称下方；电脑角色按人设固定，名号表见 titles.ts）
  look: string; // 出图用的外形描述（发型发色、主色、标志物）
  voice: { id: string; speed?: number; pitch?: number }; // MiniMax 系统音色
  style: BotStyle;
  lines: Record<LineEvent, string[]>; // 只有催促与闲聊
}

/** 点数怎么念：数字念中文，J/Q/K/A 念字母，王念大王小王。 */
export const RANK_SPOKEN: Record<number, string> = {
  2: '二', 3: '三', 4: '四', 5: '五', 6: '六', 7: '七', 8: '八', 9: '九', 10: '十', 11: 'J', 12: 'Q', 13: 'K', 14: 'A', 16: '小王', 17: '大王',
};
/** 播报员的出牌播报：单张报点数，对子报“对几”，其他报牌型；炸弹统一报“炸弹”。 */
export const CALL_LINES: Record<string, string[]> = {
  ...Object.fromEntries(Object.entries(RANK_SPOKEN).map(([r, t]) => [`play_single_${r}`, [t]])),
  ...Object.fromEntries(Object.entries(RANK_SPOKEN).map(([r, t]) => [`play_pair_${r}`, [`对${t}`]])),
  play_triple: ['三个'],
  play_fullHouse: ['三带对'],
  play_straight: ['顺子'],
  play_tube: ['连对'],
  play_plate: ['钢板'],
  play_bomb: ['炸弹！'],
  play_straightFlush: ['同花顺！'],
  play_jokerBomb: ['四大天王！'],
  pass: ['不要', '过', '要不起'],
  // 牌局事实
  first: ['上游！'],
  report: ['报牌！'],
  lastCards: ['还剩两张！'],
  borrowWind: ['借风。'],
  tribute: ['进贡。'],
  returnCard: ['还牌。'],
  resist: ['两个大王，抗贡！'],
  roundUs: ['我方升级！'],
  roundThem: ['对方升级。'],
  matchWin: ['问鼎武林！恭喜过 A！'],
  matchLose: ['对方过 A，下回再战！'],
};

export const ROSTER: Character[] = [
  {
    id: 'laogu', name: '老顾', avatar: '顾', tint: '#bfe0c9', tagline: '退休老茶客，沉得住气', title: '同花顺长老',
    look: '六十多岁的爷爷，银白短发、圆脸笑眯眯，黛绿色中式对襟马褂，手捧一只紫砂小茶壶',
    voice: { id: 'Chinese (Mandarin)_Reliable_Executive', speed: 0.95 },
    style: { bombAt: 6, wildAt: 8, ownBombAt: 8 },
    lines: {
      urge: ['不急，慢慢想。', '想好了再出。'], idle: ['这茶泡得正好。', '打牌就跟喝茶一样，急不得。'],
    },
  },
  {
    id: 'leige', name: '雷哥', avatar: '雷', tint: '#f9c9ae', tagline: '出租车司机，爱炸的急性子', title: '炸弹堂主',
    look: '三十出头的大哥，棕红色寸头、浓眉大眼，橙色工装夹克，领口挂一副墨镜',
    voice: { id: 'Chinese (Mandarin)_Unrestrained_Young_Man', speed: 1.1 },
    style: { bombAt: 12, wildAt: 12, ownBombAt: 14 },
    lines: {
      urge: ['快点快点，红灯都变绿了！', '兄弟，想啥呢？'], idle: ['今天跑车跑累了，打两把放松放松。', '谁有炸弹赶紧亮出来！'],
    },
  },
  {
    id: 'suxiaosuan', name: '苏小算', avatar: '苏', tint: '#bfe3e6', tagline: '会计姑娘，牌都记在心里', title: '顺子剑客',
    look: '二十五岁的姑娘，栗棕色高马尾，薄荷绿开衫配白衬衫，圆框眼镜，发夹是一个小算盘',
    voice: { id: 'Chinese (Mandarin)_Crisp_Girl' },
    style: { bombAt: 8, wildAt: 10, ownBombAt: 10 },
    lines: {
      urge: ['慢慢算，不着急。', '要不要我帮你算算？'], idle: ['大王还没出呢……', '我在数牌，先别打扰我。'],
    },
  },
  {
    id: 'wangayi', name: '王阿姨', avatar: '王', tint: '#f4c4d6', tagline: '广场舞领队，嘴不停手也不停', title: '对子侠',
    look: '五十多岁的阿姨，酒红色烫卷短发，玫红色丝巾配浅紫开衫，手拿一把红色舞扇',
    voice: { id: 'Chinese (Mandarin)_Kind-hearted_Antie' },
    style: { bombAt: 9, wildAt: 10, ownBombAt: 11 },
    lines: {
      urge: ['哎呀快点嘛，阿姨等得花都谢了。', '乖乖，想这么久哟！'], idle: ['你们说今晚吃什么好？', '这把打完我得去跳舞了。'],
    },
  },
  {
    id: 'xiaozhou', name: '小周', avatar: '周', tint: '#c6daf6', tagline: '大学生，刚学会规则手气倒好', title: '码牌学徒',
    look: '二十岁的男大学生，亚麻浅金色蓬松短发，天蓝色连帽卫衣，脖子上挂一副白色头戴耳机',
    voice: { id: 'male-qn-daxuesheng' },
    style: { bombAt: 10, wildAt: 12, ownBombAt: 12 },
    lines: {
      urge: ['是不是网卡了？', '你也在研究规则吗？'], idle: ['掼蛋比我想的难多了。', '我这把应该能赢吧？'],
    },
  },
  {
    id: 'chenshifu', name: '陈师傅', avatar: '陈', tint: '#cdd6ef', tagline: '掼蛋老手，一口方言腔', title: '天王盟主',
    look: '五十岁的师傅，灰白寸头、络腮短胡，藏青色工装外套，袖口别一支红笔，手边一只白色搪瓷缸',
    voice: { id: 'Chinese (Mandarin)_Humorous_Elder' },
    style: { bombAt: 7, wildAt: 9, ownBombAt: 9 },
    lines: {
      urge: ['快点，搞么斯呢？', '想好没得？'], idle: ['掼蛋讲究的就是一个掼字。', '打牌要稳，出手要狠。'],
    },
  },
  {
    id: 'linjie', name: '林姐', avatar: '林', tint: '#dccbf0', tagline: '公司总监，冷静果断', title: '钢板金刚',
    look: '三十五岁的职场女性，深棕色利落波波头，酒红色西装外套配白色内搭，一对珍珠耳环',
    voice: { id: 'female-yujie' },
    style: { bombAt: 8, wildAt: 9, ownBombAt: 10 },
    lines: {
      urge: ['时间不等人。', '请尽快出牌。'], idle: ['这局节奏还可以。', '注意配合。'],
    },
  },
  {
    id: 'afu', name: '阿福', avatar: '福', tint: '#f3dc95', tagline: '早点铺老板，乐呵呵的福气人', title: '对子侠',
    look: '四十五岁的胖乎乎大叔，圆脸光头、笑口常开，戴白色厨师帽、系明黄色围裙，手里捧一个热包子',
    voice: { id: 'Chinese (Mandarin)_Southern_Young_Man' },
    style: { bombAt: 8, wildAt: 10, ownBombAt: 10 },
    lines: {
      urge: ['不急不急，包子还热着呢。', '慢慢来，好饭不怕晚。'], idle: ['明早来我店里吃包子啊！', '打牌开心最重要。'],
    },
  },
];

export const characterById = (id: string): Character | undefined => ROSTER.find((c) => c.id === id);

/**
 * 播报员（作者：跟牌有关的播报用一个通用声音；新局开启不要角色语音，要一条有记忆点、有趣的开场语音）：
 * 播报男声，新一轮第一副开场三选一，平时报牌与牌局事实。
 */
export const NARRATOR = {
  id: 'narrator', name: '播报员', voice: { id: 'Chinese (Mandarin)_Male_Announcer', speed: 1.05 },
  lines: {
    opening: [
      '四位侠客请落座！今天谁能一路掼上论道台？开——掼！',
      '牌已洗好，茶已泡好，掼蛋开局！',
      '同舟共济，一掼到底！新一轮，开始！',
    ],
    // 晋升播报（作者 11-03）：名号升到第 n 级（1 码牌学徒 … 7 天王盟主）念第 n 句，和 titles.ts 的名号表一一对应
    promote: [
      '晋升码牌学徒！牌码得齐，心也就齐了。',
      '晋升对子侠！成双成对，出手不落单。',
      '晋升顺子剑客！一剑连五张，招招接得上。',
      '晋升钢板金刚！三同连张，硬得像块钢板。',
      '晋升炸弹堂主！手里有炸，说话都有底气。',
      '晋升同花顺长老！同花连顺，江湖罕见。',
      '晋升天王盟主！四大天王在手，号令江湖。',
    ],
    ...CALL_LINES,
  } as Record<string, string[]>,
};

/** 默认座位：对家老顾（搭档）、下家雷哥、上家苏小算；开局设置里可以换。 */
export const DEFAULT_CAST: Record<1 | 2 | 3, string> = { 2: 'laogu', 1: 'leige', 3: 'suxiaosuan' };

/** 兼容旧接口：按座位取角色（含 seat 字段），测试与机器人风格沿用。 */
export interface Persona extends Character { seat: 1 | 2 | 3 }
let cast: Record<1 | 2 | 3, string> = { ...DEFAULT_CAST };
export let PERSONAS: Persona[] = buildPersonas();
function buildPersonas(): Persona[] {
  return ([2, 1, 3] as const).map((seat) => ({ ...characterById(cast[seat])!, seat }));
}
/** 开局设置时换牌友。 */
export function setCast(next: Record<1 | 2 | 3, string>): void {
  cast = { ...next };
  PERSONAS = buildPersonas();
}
export const personaOf = (seat: number): Persona | undefined => PERSONAS.find((p) => p.seat === seat);
